import React from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { StyledBlackInformationTypography, StyledDarkRedInformationTypography } from "../../../components/commonComponents/StyledComponents/styled";
import TextInputField from "../../../components/commonComponents/TextInputs/TextInputField";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../Store/actions";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { Close, Save } from "@mui/icons-material";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import {
  checkUserDetailsFromCookie,
  checkWorkSpaceFromCookies,
  saveBrandInformation,
} from "../../../api_calls/userManagement";
import {
  getBottomNavRetailerSelectionFromCookies,
  isLockedOutsideAllRetailers,
  resolveRetailerSelectionOptions,
} from "../../../api_calls/retailerSelection";
import { fetchCompanies, fetchCompanyHierarchy } from "../../../api_calls/companyRequests";
import { toast } from "react-toastify";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import CompanyHierarchySingleSelect from "../../../components/commonComponents/Selectors/CompanyHierarchySingleSelect";
import SharedCampaignList from "./SharedCampaignList";
import Confirmation from "../../../components/commonComponents/Confirmation";
import {
  ADD_TO_NEW_COMPANY,
  ALREADY_ADDED_COMPANY_TOOLTIP,
  isAddToNewCompanyOption,
  isAlreadyAddedCompany,
  keepShareableHierarchy,
  markAlreadyAddedCompanies,
  toCompanyOption,
} from "./companyPickerUtils";

// After prune, remaining false rows are ancestor headers only (grey + tooltip).
const isCompanyWithoutShareableCampaigns = (company) =>
  company?.has_shareable_campaign === false;

const NO_SHAREABLE_CAMPAIGNS_TOOLTIP = "No shareable campaigns for this company";

const isNewCompanyRowDisabled = (company) =>
  isAlreadyAddedCompany(company) === true || isCompanyWithoutShareableCampaigns(company) === true;

const newCompanyDisabledTooltip = (company) =>
  isAlreadyAddedCompany(company) === true
    ? ALREADY_ADDED_COMPANY_TOOLTIP
    : NO_SHAREABLE_CAMPAIGNS_TOOLTIP;

const AddBrandUser = ({ open, onClose, usedFor, userInfo, closeModal, cyreenBoolean, refreshUserType }) => {
  const currentStateData = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();

  const [userDetails, setUserDetails] = React.useState(userInfo);
  const [fieldError, setFieldError] = React.useState({});
  const [showCancelConfirmation, setShowCancelConfirmation] = React.useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = React.useState(false);
  const [isCompanyReady, setIsCompanyReady] = React.useState(false);

  // Create: full shareable tree. Edit New Company: already-assigned companies stay as grey "(added)" rows.
  const [hierarchyRetailers, setHierarchyRetailers] = React.useState([]);
  const [newCompanyTree, setNewCompanyTree] = React.useState([]);
  const [selectedHierarchyCompany, setSelectedHierarchyCompany] = React.useState(null);
  const [selectedNewCompany, setSelectedNewCompany] = React.useState(null);
  const [openNewCompanyDropdown, setOpenNewCompanyDropdown] = React.useState(false);

  const contentRef = React.useRef(null);

  const checkUserDetail = checkUserDetailsFromCookie();
  const userData = checkUserDetail.success === true ? checkUserDetail.user_detail : {};
  const loggedInUserType = typeof userData.user_type !== "undefined" ? userData.user_type.toLowerCase() : "";
  const checkCurrentWorkSpaces = checkWorkSpaceFromCookies();

  const companyOptionsById =
    typeof currentStateData.company_id_option_obj !== "undefined" ? currentStateData.company_id_option_obj : {};

  const bottomNavRetailerSelection =
    typeof currentStateData.bottom_nav_selected_retailer !== "undefined"
      ? currentStateData.bottom_nav_selected_retailer
      : getBottomNavRetailerSelectionFromCookies();
  const lockedToSpecificRetailer = isLockedOutsideAllRetailers(
    bottomNavRetailerSelection,
    resolveRetailerSelectionOptions(currentStateData)
  );

  // Tenant, customer, agency, and branch do not pick a company. Only Cyreen can,
  // unless a specific retailer is selected in the bottom nav.
  const canPickCompanies = loggedInUserType === "cyreen";
  const showCompanyField = canPickCompanies === true && lockedToSpecificRetailer !== true;

  const isAddingToNewCompany = isAddToNewCompanyOption(selectedHierarchyCompany);

  const companiesFromUser = () => {
    if (Array.isArray(userInfo.assigned_companies) && userInfo.assigned_companies.length > 0) {
      return userInfo.assigned_companies;
    }
    if (typeof userInfo.assigned_company !== "undefined" && userInfo.assigned_company !== null) {
      return [userInfo.assigned_company];
    }
    return [];
  };

  const assignedCompanyFlatOptions = companiesFromUser()
    .map((company) => toCompanyOption(company))
    .sort((first, second) => String(first.label).localeCompare(String(second.label)));

  const handleFieldChange = (user_data) => {
    setUserDetails((previousDetails) => {
      const updatedDetails = {
        ...previousDetails,
        ...user_data,
      };
      dispatchAction(updateStateData(updatedDetails));
      return updatedDetails;
    });
  };

  // Company whose campaigns we show / send as workspace_id
  const applyCompanySelection = (option, campaignIds = [], markInteracted = true) => {
    const companyId = option?.id;
    const isRealCompany = typeof companyId !== "undefined" && companyId !== null && companyId !== ADD_TO_NEW_COMPANY.id;

    if (isRealCompany === true && typeof fieldError.user_company !== "undefined") {
      setFieldError((previousErrors) => {
        const { user_company, ...remainingErrors } = previousErrors;
        return remainingErrors;
      });
    }

    handleFieldChange({
      ...(markInteracted === true ? { user_interacted: true } : {}),
      selected_company_id: isRealCompany === true ? companyId : "",
      user_assigned_company:
        isRealCompany === true
          ? typeof companyOptionsById[companyId] !== "undefined"
            ? companyOptionsById[companyId]
            : { id: companyId, name: option.label }
          : {},
      brand_campaign_selection: isRealCompany === true ? campaignIds : [],
    });
  };

  const handleCreateCompanyChange = (option) => {
    setSelectedHierarchyCompany(option);
    // New company means a new campaign list — start with nothing ticked
    applyCompanySelection(option, []);
  };

  const handleEditCompanyChange = (option) => {
    if (option === null) {
      setSelectedHierarchyCompany(null);
      setSelectedNewCompany(null);
      setOpenNewCompanyDropdown(false);
      applyCompanySelection(null, []);
      return;
    }

    if (isAddToNewCompanyOption(option)) {
      setSelectedHierarchyCompany(ADD_TO_NEW_COMPANY);
      setSelectedNewCompany(null);
      setOpenNewCompanyDropdown(true);
      applyCompanySelection(null, []);
      return;
    }

    setSelectedHierarchyCompany(option);
    setSelectedNewCompany(null);
    setOpenNewCompanyDropdown(false);
    applyCompanySelection(option, option.campaign_ids || []);
  };

  const handleNewCompanyChange = (option) => {
    setSelectedNewCompany(option);
    setOpenNewCompanyDropdown(false);
    applyCompanySelection(option, []);
  };

  const selectedCompanyId =
    isAddingToNewCompany === true ? selectedNewCompany?.id : selectedHierarchyCompany?.id;

  const selectedCompanyForList =
    isAddingToNewCompany === true ? selectedNewCompany : selectedHierarchyCompany;

  const hasRealCompanySelected =
    typeof selectedCompanyId !== "undefined" &&
    selectedCompanyId !== "" &&
    selectedCompanyId !== ADD_TO_NEW_COMPANY.id;

  const handleDialogClose = () => {
    if (typeof currentStateData.user_interacted !== "undefined" && currentStateData.user_interacted === true) {
      setShowCancelConfirmation(true);
    } else {
      setShowCancelConfirmation(false);
      onClose();
    }
  };

  const handleSaveFunction = () => {
    const originalCampaignIds = Array.isArray(selectedCompanyForList?.campaign_ids)
      ? selectedCompanyForList.campaign_ids
      : [];
    const campaignIdsFromState = Array.isArray(currentStateData.brand_campaign_selection)
      ? currentStateData.brand_campaign_selection
      : [];

    const campaignCountChanged = originalCampaignIds.length !== campaignIdsFromState.length;
    if (campaignCountChanged === true) {
      setShowSaveConfirmation(true);
    } else {
      saveBrandInfo();
    }
  };

  const saveBrandInfo = () => {
    setFieldError({});
    const userId = { id: typeof userDetails.id !== "undefined" ? userDetails.id : "" };
    saveBrandInformation(usedFor, userId, refreshUserType).then((info) => {
      if (info.success === true) {
        const message = usedFor === "update" ? "User updated successfully" : "User added successfully";
        toast(message, { type: "success" });
        closeModal();
      } else {
        setFieldError(info.errorMessages);
      }
    });
  };

  const pickDefaultAssignedCompany = () => {
    const assigned = companiesFromUser();
    if (assigned.length === 0) {
      return null;
    }

    const loggedInCompanyId = userData?.assigned_company?.id;
    const matchLoggedIn = assigned.find((company) => company.id === loggedInCompanyId);
    const chosen = matchLoggedIn || [...assigned].sort((a, b) => a.name.localeCompare(b.name))[0];
    return toCompanyOption(chosen);
  };

  const resolveCreateDefaultCompany = () => {
    if (lockedToSpecificRetailer === true) {
      const retailerId =
        typeof bottomNavRetailerSelection.id !== "undefined" && bottomNavRetailerSelection.id !== null
          ? bottomNavRetailerSelection.id
          : bottomNavRetailerSelection.value;
      const retailerLabel =
        typeof bottomNavRetailerSelection.label !== "undefined"
          ? bottomNavRetailerSelection.label
          : String(retailerId);
      return { id: retailerId, label: retailerLabel };
    }

    if (checkCurrentWorkSpaces.success === true && typeof checkCurrentWorkSpaces.current_workspace?.id !== "undefined") {
      return {
        id: checkCurrentWorkSpaces.current_workspace.id,
        label: checkCurrentWorkSpaces.current_workspace.name,
      };
    }

    if (typeof userData.assigned_company !== "undefined" && userData.assigned_company !== null) {
      return {
        id: userData.assigned_company.id,
        label: userData.assigned_company.name,
      };
    }

    return null;
  };

  React.useEffect(() => {
    const initializeBrandModal = async () => {
      try {
        // Share-list and hierarchy are only for the visible company picker (Cyreen).
        if (showCompanyField === true) {
          fetchCompanies("users");

          const hierarchyInfo = await fetchCompanyHierarchy({ viewCampaignShare: true });
          // Brand create keeps the tenant/root node. Users-tab create strips it.
          const fullTree = hierarchyInfo.success === true ? hierarchyInfo.retailers : [];
          // Hide companies with nothing to share. Ancestors of a shareable
          // descendant stay as disabled headers so the tree path is still visible.
          const shareableTree = keepShareableHierarchy(fullTree);
          setHierarchyRetailers(shareableTree);

          const assignedIds = companiesFromUser().map((company) => company.id);
          setNewCompanyTree(markAlreadyAddedCompanies(shareableTree, assignedIds));
        }

        handleFieldChange({
          ...userInfo,
          cyreen_user: cyreenBoolean,
          selected_company_id: "",
          brand_campaign_selection: [],
        });

        // Company* starts empty when the picker is shown. When it is hidden, add uses
        // the logged-in company and edit uses the brand user's assigned company.
        if (showCompanyField !== true) {
          if (usedFor === "update") {
            const defaultAssigned = pickDefaultAssignedCompany();
            if (defaultAssigned !== null) {
              setSelectedHierarchyCompany(defaultAssigned);
              applyCompanySelection(defaultAssigned, defaultAssigned.campaign_ids || [], false);
            }
          } else {
            const defaultCreate = resolveCreateDefaultCompany();
            if (defaultCreate !== null) {
              setSelectedHierarchyCompany(defaultCreate);
              applyCompanySelection(defaultCreate, [], false);
            }
          }
        }
      } finally {
        setIsCompanyReady(true);
      }
    };

    initializeBrandModal();
  }, []);

  return (
    <Dialog
      open={open}
      onClose={(e) => {
        e.preventDefault();
      }}
      fullWidth={true}
      maxWidth={"lg"}
    >
      <DialogTitle>{usedFor === "update" ? "Edit Brand" : "Add Brand"}</DialogTitle>
      <DialogContent ref={contentRef}>
        <Stack direction={"row"} spacing={2}>
          {isCompanyReady === true && showCompanyField === true && (
            <>
              {usedFor === "update" ? (
                <SingleSelect
                  label="Company*"
                  enableFullWidth={true}
                  disableSearch={true}
                  sortOptions={false}
                  allowClearance={true}
                  selectionCheck={(option, value) => option.id === value.id}
                  leadingOption={ADD_TO_NEW_COMPANY}
                  options={assignedCompanyFlatOptions}
                  defaultVal={isAddingToNewCompany === true ? ADD_TO_NEW_COMPANY : selectedHierarchyCompany}
                  onChangeCall={handleEditCompanyChange}
                  fieldError={
                    typeof fieldError.user_company !== "undefined" && fieldError.user_company !== ""
                      ? fieldError.user_company
                      : ""
                  }
                />
              ) : (
                <CompanyHierarchySingleSelect
                  label="Company*"
                  enableFullWidth={true}
                  showSearch={true}
                  retailers={hierarchyRetailers}
                  value={selectedHierarchyCompany}
                  onChange={handleCreateCompanyChange}
                  disabledOptions={isCompanyWithoutShareableCampaigns}
                  tooltipTitleForDisabledOptions={NO_SHAREABLE_CAMPAIGNS_TOOLTIP}
                  fieldError={
                    typeof fieldError.user_company !== "undefined" && fieldError.user_company !== ""
                      ? fieldError.user_company
                      : ""
                  }
                />
              )}
              {usedFor === "update" && isAddingToNewCompany === true && (
                <CompanyHierarchySingleSelect
                  label="New Company*"
                  enableFullWidth={true}
                  showSearch={true}
                  autoOpen={openNewCompanyDropdown}
                  retailers={newCompanyTree}
                  value={selectedNewCompany}
                  onChange={handleNewCompanyChange}
                  disabledOptions={isNewCompanyRowDisabled}
                  tooltipTitleForDisabledOptions={newCompanyDisabledTooltip}
                  fieldError={
                    typeof fieldError.user_company !== "undefined" && fieldError.user_company !== ""
                      ? fieldError.user_company
                      : ""
                  }
                />
              )}
            </>
          )}

          <TextInputField
            fieldRequired={true}
            elemId="email"
            readOnlyFlag={usedFor === "update" ? true : false}
            label="E-Mail"
            fieldName="email"
            showlabel={true}
            defaultVal={typeof userDetails.email !== "undefined" ? userDetails.email : ""}
            inputType={"text"}
            onChangeCall={(e) => {
              e.preventDefault();
              const value = e.target.value;
              if (value.includes(" ")) {
                setFieldError({ ...fieldError, email: "E-Mail must not contain spaces" });
              } else if (fieldError.email) {
                const { email, ...rest } = fieldError;
                setFieldError(rest);
              }
              handleFieldChange({
                user_interacted: true,
                email: value.replace(/\s/g, ""),
              });
            }}
            fieldError={typeof fieldError.email !== "undefined" && fieldError.email !== "" ? fieldError.email : ""}
          />
        </Stack>

        <Box height={10} />
        <StyledBlackInformationTypography variant="h6" sx={{ marginLeft: 0, fontWeight: "normal" }}>
          Shared Campaign Reports
        </StyledBlackInformationTypography>
        <StyledBlackInformationTypography variant="body2" sx={{ marginLeft: 0, fontWeight: "normal" }}>
          Select all campaign reports that this brand user should have access to. Additional settings for campaign-specific
          filters applied to the brands can be changed in the Brand Report settings.
        </StyledBlackInformationTypography>
        <Box height={5} />

        <SharedCampaignList
          ownerId={selectedCompanyId}
          hasRealCompanySelected={hasRealCompanySelected === true}
          companyObj={selectedCompanyForList || {}}
          fieldError={
            typeof fieldError.campaign_id !== "undefined" && fieldError.campaign_id !== ""
              ? fieldError.campaign_id
              : ""
          }
          contentRef={contentRef}
        />
      </DialogContent>

      <Box height={20} />
      <DialogActions>
        <Stack direction={"row"} spacing={2}>
          <CloseButton onClick={handleDialogClose} label="Cancel" icon={<Close />} />
          <ActionButton
            onClick={(e) => {
              e.preventDefault();
              handleSaveFunction();
            }}
            label={usedFor === "update" ? "Update" : "Save"}
            icon={<Save />}
          />
        </Stack>
      </DialogActions>

      {showSaveConfirmation === true && (
        <Confirmation
          showConfirmationPopup={showSaveConfirmation}
          closePopup={() => {
            setShowSaveConfirmation(false);
          }}
          confirmationActionCall={() => {
            setShowSaveConfirmation(false);
            saveBrandInfo();
          }}
          confirmationMessage={
            <>
              <Typography variant="body2">You are about to share reports with external parties.</Typography>
              <Box height={10} />
              <Typography variant="body2">Please confirm that all information is correct before proceeding.</Typography>
            </>
          }
          confirmationTitle={"Confirm Action"}
        />
      )}

      {showCancelConfirmation === true && (
        <Confirmation
          showConfirmationPopup={showCancelConfirmation}
          closePopup={() => {
            setShowCancelConfirmation(false);
          }}
          confirmationActionCall={() => {
            setShowCancelConfirmation(false);
            onClose();
          }}
          confirmationMessage={
            <>
              <Typography variant="body2">Are you certain you want to discard all the changes you have made?</Typography>
            </>
          }
          confirmationTitle={"Confirm Action"}
        />
      )}
    </Dialog>
  );
};

export default AddBrandUser;
